import { CloseOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { GlobalToken } from "antd";
import { FC, ReactNode, useEffect, useRef } from "react";
import { styled } from "styled-components";
import { AgentState } from "../../models/agent";
import AppBaseLayout from "./AppBaseLayout";
import CenterContent from "./CenterContent";

interface IProps {
  displayMenuButton: boolean;
  mainContent: ReactNode;
  onBusinessUnitsPage: boolean;
  menuContent?: ReactNode;
  agentState?: AgentState;
  isMenuOpen?: boolean;
  onMenuOpenChange?(isOpen: boolean): void;
}

const LayoutWithNav: FC<IProps> = ({ menuContent, mainContent, onBusinessUnitsPage, agentState, displayMenuButton, isMenuOpen, onMenuOpenChange }) => {
  const sideMenuContainerRef = useRef<HTMLDivElement>(null);

  useEffect(
    () => {
      const handleTouchStart = (event: TouchEvent) => {
        if (sideMenuContainerRef.current && !sideMenuContainerRef.current.contains(event.target as Node))
          onMenuOpenChange?.(false);
      };

      document.addEventListener("touchstart", handleTouchStart, true);
      return () => document.removeEventListener("touchstart", handleTouchStart, true);
    },
    [onMenuOpenChange],
  );

  return (
    <AppBaseLayout
      appContent={
        <AppContainer>
          {
            displayMenuButton && onMenuOpenChange &&
            <MenuButtonContainer $isMenuOpen={false} $onBusinessUnitsPage={onBusinessUnitsPage} $agentState={agentState}>
              <UnorderedListOutlined onClick={() => onMenuOpenChange(true)} />
            </MenuButtonContainer>
          }
          <CenterContent>
            {mainContent}
          </CenterContent>
          {
            isMenuOpen && onMenuOpenChange &&
            <SideMenuContainer ref={sideMenuContainerRef}>
              <MenuButtonContainer $isMenuOpen={true}>
                <CloseOutlined onClick={() => onMenuOpenChange(false)} />
              </MenuButtonContainer>
              {menuContent}
            </SideMenuContainer>
          }
        </AppContainer>
      }
    />
  );
};

export default LayoutWithNav;

const AppContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const MenuButtonContainer = styled.div<{ $isMenuOpen: boolean; $onBusinessUnitsPage?: boolean; $agentState?: AgentState }>`
  width: 100%;
  display: flex;
  justify-content: ${p => p.$isMenuOpen ? "flex-end" : "flex-start"};
  align-items: center;
  font-size: ${p => p.theme.fontSizeHeading2}px;
  padding: ${p => p.theme.padding}px ${p => p.theme.padding}px ${p => p.theme.paddingXXS}px ${p => p.theme.paddingLG}px; 
  background-color: ${p => getBackgroundColor(p.theme, p.$onBusinessUnitsPage, p.$agentState) ?? p.theme.colorBgLayout};
`;

const SideMenuContainer = styled.div`
  position: absolute;
  z-index: 9999;
  top: 0;
  left: 0;
  bottom: 0;
  width: 65%;
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.paddingXL}px;
  background-color: ${p => p.theme.colorBgLayout};
  
  transform: translateX(-100%);
  animation: slideIn 0.12s cubic-bezier(0.3, 0, 0.2, 1) forwards;

  @keyframes slideIn {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0%);
    }
  }
`;

function getBackgroundColor(theme: GlobalToken, onBusinessUnitsPage?: boolean, state?: AgentState) {
  if (onBusinessUnitsPage) {
    return theme.colorPrimaryBg;
  }

  if (state) {
    switch (state) {
      case "Idle":
        return theme.colorSuccess;
      case "Break":
        return theme.colorWarning;
      case "Busy":
        return theme.colorPrimary;
      case "LoggedOut":
      case "PendingBreak":
      case "PendingLogout":
      default:
        return theme.colorPrimaryBg;
    }
  }
}
